import React, { useState, useEffect, useCallback } from 'react';
import { openWhatsApp } from '../../lib/whatsapp';
import { useChat } from './chat/useChat';
import { FloatingButtons } from './chat/FloatingButtons';
import { ChatHeader } from './chat/ChatHeader';
import { ChatMessages } from './chat/ChatMessages';
import { ChatInput } from './chat/ChatInput';
import { HandoffButton } from './chat/HandoffButton';
import { MAX_USER_TURNS, SCROLL_REVEAL_THRESHOLD, buildHandoffMessage, countUserTurns } from './chat/chatUtils';

export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { messages, isLoading, isDone, initChat, sendUserMessage } = useChat();

  // Reveal FABs after user scrolls past hero (avoid overlapping hero CTA on mobile)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > SCROLL_REVEAL_THRESHOLD);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Broadcast open/close so StickyMobileCTA can hide while chat is up.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('chat:toggle', { detail: { open: isOpen } }));
  }, [isOpen]);

  // Kick off greeting on first open.
  useEffect(() => {
    if (isOpen && messages.length === 0 && !isLoading) initChat();
  }, [isOpen, messages.length, isLoading, initChat]);

  const userTurns = countUserTurns(messages);
  const limitReached = userTurns >= MAX_USER_TURNS;

  const handleOpenChat = useCallback(() => setIsOpen(true), []);

  const handleDirectWhatsApp = useCallback(() => {
    openWhatsApp('Namaste Nitika Ji, I would like to book a consultation.');
  }, []);

  const handleHandoff = useCallback(() => {
    openWhatsApp(buildHandoffMessage(messages));
  }, [messages]);

  const handleSend = useCallback(
    (text) => {
      if (isLoading || isDone || limitReached) return;
      sendUserMessage(text);
    },
    [isLoading, isDone, limitReached, sendUserMessage]
  );

  return (
    <>
      <FloatingButtons
        visible={scrolled}
        isOpen={isOpen}
        onOpenChat={handleOpenChat}
        onDirectWhatsApp={handleDirectWhatsApp}
      />

      {isOpen && (
        <div
          className="fixed bottom-4 right-4 md:bottom-24 md:right-8 z-[65] w-[calc(100vw-32px)] max-w-[380px] h-[calc(100vh-40px)] max-h-[600px] md:h-[70vh] md:max-h-[560px] bg-[#0A0000] border border-[#D4AF37]/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="chat-panel"
          style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 40px rgba(212,175,55,0.08)' }}
        >
          <ChatHeader onClose={() => setIsOpen(false)} />
          <ChatMessages messages={messages} isLoading={isLoading} />
          {(isDone || limitReached) && <HandoffButton onClick={handleHandoff} />}
          {!isDone && !limitReached && (
            <ChatInput onSend={handleSend} disabled={isLoading} userTurns={userTurns} />
          )}
        </div>
      )}
    </>
  );
};
